/**
 * 各平台共用的工具函数，单一来源，便于维护与修复 Bug。
 * 使用方式：import shared from './core/shared'; c.helper = { ...shared, getCart, ... };
 */
import isKnownCrawlerUA from './isKnownCrawlerUA';
import storage from './storage';
import storageKeys from './storageKeys';

function getCookie(c_name) {
  if (document.cookie.length > 0) {
    var c_start = document.cookie.indexOf(c_name + '=');
    if (c_start !== -1) {
      c_start = c_start + c_name.length + 1;
      var c_end = document.cookie.indexOf(';', c_start);
      if (c_end === -1) c_end = document.cookie.length;
      return unescape(document.cookie.substring(c_start, c_end));
    }
  }
  return '';
}

function getQueryVariable(variable) {
  const urlSearchParams = new URLSearchParams(location.search.replace(/\+/g, '%2B'));
  return urlSearchParams.get(variable) || '';
}

function S4() {
  return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
}

function guid() {
  return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
}

var XHR_TIMEOUT_MS = 20 * 1000;
var XHR_RETRY_MAX = 2;   // 最多重试 2 次，共 3 次请求（与 react-plugin 一致）
var XHR_RETRY_DELAY_MS = 1000;

function ajaxGet(data, fn, errFn, retryCount) {
  retryCount = retryCount || 0;
  var xhr = new XMLHttpRequest();
  xhr.open('GET', data.url, true);
  xhr.timeout = XHR_TIMEOUT_MS;
  xhr.onreadystatechange = function () {
    if (xhr.readyState === 4) {
      var responseText;
      try {
        responseText = JSON.parse(xhr.responseText);
      } catch (e) {
        responseText = xhr.responseText;
      }
      if (xhr.status === 200 || xhr.status === 304) {
        fn && fn.call(this, responseText);
      } else {
        var isRetryable = xhr.status === 0 || xhr.status >= 500;
        if (isRetryable && retryCount < XHR_RETRY_MAX) {
          setTimeout(function () { ajaxGet(data, fn, errFn, retryCount + 1); }, XHR_RETRY_DELAY_MS);
        } else {
          errFn && errFn.call(this, responseText);
        }
      }
    }
  };
  xhr.ontimeout = function () {
    if (retryCount < XHR_RETRY_MAX) {
      setTimeout(function () { ajaxGet(data, fn, errFn, retryCount + 1); }, XHR_RETRY_DELAY_MS);
    } else {
      errFn && errFn.call(this, null);
    }
  };
  xhr.send();
}

function ajaxPost(data, fn, errFn, retryCount) {
  retryCount = retryCount || 0;
  var xhr = new XMLHttpRequest();
  xhr.open('POST', data.url, true);
  xhr.timeout = XHR_TIMEOUT_MS;
  xhr.setRequestHeader('Content-Type', data.contentType || 'application/json');
  xhr.onreadystatechange = function () {
    if (xhr.readyState === 4) {
      if (xhr.status === 200 || xhr.status === 304) {
        var responseText;
        try {
          responseText = JSON.parse(xhr.responseText);
        } catch (e) {
          responseText = xhr.responseText;
        }
        fn && fn.call(this, responseText);
      } else {
        var isRetryable = xhr.status === 0 || xhr.status >= 500;
        if (isRetryable && retryCount < XHR_RETRY_MAX) {
          setTimeout(function () { ajaxPost(data, fn, errFn, retryCount + 1); }, XHR_RETRY_DELAY_MS);
        } else {
          var responseText;
          try {
            responseText = xhr.responseText ? JSON.parse(xhr.responseText) : null;
          } catch (e) {
            responseText = xhr.responseText;
          }
          errFn && errFn.call(this, responseText);
        }
      }
    }
  };
  xhr.ontimeout = function () {
    if (retryCount < XHR_RETRY_MAX) {
      setTimeout(function () { ajaxPost(data, fn, errFn, retryCount + 1); }, XHR_RETRY_DELAY_MS);
    } else {
      errFn && errFn.call(this, null);
    }
  };
  xhr.send(typeof data.data === 'string' ? data.data : JSON.stringify(data.data));
}

function safeJsonParse(str, fallback) {
  if (!str) return fallback;
  try {
    return JSON.parse(str);
  } catch (e) {
    if (console && console.error) {
      console.error('safeJsonParse error', e);
    }
    return fallback;
  }
}

/**
 * 不区分渠道：window.Shopify.locale 与 country 均存在时返回语言-地区（如 en-FR）。
 * 兜底：Shopify 全局缺失时读主题插件（onsite-javascript.liquid，仅 shopify 通道）写入的
 * localStorage cartsee_customer_locale（其值按字段依次取 Shopify 全局、浏览器语言、店铺默认值）。
 * 均无值返回 null，不上报脏数据。
 */
function getShopifyCustomerLocale() {
  var locale = window?.Shopify?.locale;
  var country = window?.Shopify?.country;
  if (locale && country) {
    return locale + '-' + country;
  }
  var cached = null;
  try {
    // localStorage 在部分隐私模式下访问即抛错，需整体 try 住
    cached = safeJsonParse(localStorage.getItem('cartsee_customer_locale'), null);
  } catch (e) {}
  if (cached && cached.languageCode && cached.countryCode) {
    // zh-Hans 等带文字体系的值只取语言码，地区码统一大写，保持 en-US 格式
    var lang = String(cached.languageCode).split('-')[0];
    return lang + '-' + String(cached.countryCode).toUpperCase();
  }
  return null;
}

/**
 * 邮箱格式校验（HTML5 标准 + RFC 5321 长度约束）。
 * 与 react-plugin 的 utils/index.isValidEmail 行为保持一致，作为 js-plugin 单一来源，
 * 供 platform 下的 newsletter 等模块复用，避免各文件各写一份 isEmail。
 */
function isValidEmail(email) {
  if (!email || email.length > 254) return false; // RFC 5321 规定邮箱最大长度 254
  // HTML5 标准邮箱验证正则
  var emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  if (!emailRegex.test(email)) return false;
  // 额外检查：确保 @ 符号前后都有内容，且域名至少有一个点
  var parts = email.split('@');
  if (parts.length !== 2) return false;
  var localPart = parts[0];
  var domain = parts[1];
  if (!localPart || !domain) return false;
  if (localPart.length > 64) return false; // RFC 5321 规定本地部分最大 64 字符
  if (!domain.includes('.')) return false; // 域名必须包含至少一个点
  if (domain.startsWith('.') || domain.endsWith('.')) return false; // 域名不能以点开始或结束
  if (domain.includes('..')) return false; // 域名不能有连续的点
  return true;
}

function log(...args) {
  if ((storage.getLocal(storageKeys.DEBUG) || '').includes('cartsee:')) {
    console.log('%ccartsee:carttrack', 'color: #513EFF', ...args);
  }
};

export default {
  getCookie,
  getQueryVariable,
  S4,
  guid,
  getShopifyCustomerLocale,
  isKnownCrawlerUA,
  isValidEmail,
  ajax: {
    get: ajaxGet,
    post: ajaxPost
  },
  safeJsonParse,
  log
};
